import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as productService from '../../services/productService';

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params, { rejectWithValue }) => {
    try {
      const res = await productService.getProducts(params);
      const body = res.data;
      return {
        products: body.data || body.products || [],
        totalPages: body.pagination?.totalPages || body.totalPages || 1,
        currentPage: body.pagination?.page || body.page || 1,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchProduct = createAsyncThunk(
  'products/fetchProduct',
  async (id, { rejectWithValue }) => {
    try {
      const res = await productService.getProduct(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch product');
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const res = await productService.createProduct(productData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create product');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      const res = await productService.updateProduct(id, productData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update product');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id, { rejectWithValue }) => {
    try {
      await productService.deleteProduct(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete product');
    }
  }
);

export const addStock = createAsyncThunk(
  'products/addStock',
  async ({ id, quantity, note }, { rejectWithValue }) => {
    try {
      const res = await productService.addStock(id, { quantity, note });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add stock');
    }
  }
);

export const receiveStock = createAsyncThunk(
  'products/receiveStock',
  async ({ id, boxes, halfBoxes, packs, pieces, notes }, { rejectWithValue }) => {
    try {
      const res = await productService.receiveStock(id, { boxes, halfBoxes, packs, pieces, notes });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to receive stock');
    }
  }
);

export const removeStock = createAsyncThunk(
  'products/removeStock',
  async ({ id, quantity, note }, { rejectWithValue }) => {
    try {
      const res = await productService.removeStock(id, { quantity, note });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove stock');
    }
  }
);

export const returnStock = createAsyncThunk(
  'products/returnStock',
  async ({ id, quantity, note }, { rejectWithValue }) => {
    try {
      const res = await productService.returnStock(id, { quantity, note });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to return stock');
    }
  }
);

export const getMovements = createAsyncThunk(
  'products/getMovements',
  async (params, { rejectWithValue }) => {
    try {
      const res = await productService.getMovements(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch movements');
    }
  }
);

export const getLowStockAlerts = createAsyncThunk(
  'products/getLowStockAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await productService.getLowStockAlerts();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch alerts');
    }
  }
);

export const getExpiringAlerts = createAsyncThunk(
  'products/getExpiringAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await productService.getExpiringAlerts();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch expiring alerts');
    }
  }
);

export const resolveAlert = createAsyncThunk(
  'products/resolveAlert',
  async (id, { rejectWithValue }) => {
    try {
      const res = await productService.resolveAlert(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to resolve alert');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const res = await productService.getCategories();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const bulkImport = createAsyncThunk(
  'products/bulkImport',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await productService.bulkImport(formData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Bulk import failed');
    }
  }
);

export const exportProducts = createAsyncThunk(
  'products/exportProducts',
  async (params, { rejectWithValue }) => {
    try {
      const res = await productService.exportProducts(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Export failed');
    }
  }
);

const initialState = {
  products: [],
  product: null,
  loading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,
  categories: [],
  alerts: [],
  movements: [],
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProduct: (state) => {
      state.product = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products || action.payload.data || action.payload;
        state.totalPages = action.payload.totalPages || action.payload.pages || 1;
        state.currentPage = action.payload.page || 1;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.product = action.payload.product || action.payload;
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products.unshift(action.payload.product || action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.product || action.payload;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.product?._id === updated._id) state.product = updated;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter((p) => p._id !== action.payload);
        if (state.product?._id === action.payload) state.product = null;
      })
      .addCase(addStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStock.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.product || action.payload;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.product?._id === updated._id) state.product = updated;
      })
      .addCase(addStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(receiveStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(receiveStock.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.product || action.payload;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.product?._id === updated._id) state.product = updated;
      })
      .addCase(receiveStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeStock.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.product || action.payload;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.product?._id === updated._id) state.product = updated;
      })
      .addCase(removeStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(returnStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(returnStock.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.product || action.payload;
        const idx = state.products.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.products[idx] = updated;
        if (state.product?._id === updated._id) state.product = updated;
      })
      .addCase(returnStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMovements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMovements.fulfilled, (state, action) => {
        state.loading = false;
        state.movements = action.payload.movements || action.payload.data || action.payload;
      })
      .addCase(getMovements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getLowStockAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLowStockAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload.alerts || action.payload.data || action.payload;
      })
      .addCase(getLowStockAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getExpiringAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getExpiringAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload.alerts || action.payload.data || action.payload;
      })
      .addCase(getExpiringAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resolveAlert.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resolveAlert.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = state.alerts.filter((a) => a._id !== (action.payload.alert?._id || action.payload._id));
      })
      .addCase(resolveAlert.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.categories || action.payload.data || action.payload;
      })
      .addCase(bulkImport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkImport.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.products) {
          state.products = [...action.payload.products, ...state.products];
        }
      })
      .addCase(bulkImport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(exportProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportProducts.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearProduct, clearError, setPage } = productSlice.actions;
export default productSlice.reducer;
